import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Clock,
  Loader,
  CheckCircle,
  AlertTriangle,
  Skull,
  ChevronRight,
  RotateCcw,
  Eye,
} from 'lucide-react';
import { Button, Badge, Table, Modal, Select } from '@/components/ui';
import { integrationService } from '@/services/integrationService';
import type {
  OutboxMessage,
  OutboxMessageStatus,
  IntegrationMessageType,
  DeadLetter,
} from '@/types/integration';
import styles from './IntegrationDashboardPage.module.css';

/** Auto-refresh interval in milliseconds. */
const REFRESH_INTERVAL_MS = 10_000;

/* ── Label / badge maps ────────────────────────────────── */

const STATUS_LABELS: Record<OutboxMessageStatus, string> = {
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  COMPLETED: 'Concluído',
  FAILED: 'Falhou',
  DEAD_LETTER: 'Dead Letter',
};

const STATUS_CSS: Record<OutboxMessageStatus, string> = {
  PENDING: styles.badgePending,
  PROCESSING: styles.badgeProcessing,
  COMPLETED: styles.badgeCompleted,
  FAILED: styles.badgeFailed,
  DEAD_LETTER: styles.badgeDeadLetter,
};

const MESSAGE_TYPE_LABELS: Record<IntegrationMessageType, string> = {
  PATIENT_SYNC: 'Sync Paciente',
  APPOINTMENT_SYNC: 'Sync Agendamento',
  SLOT_FETCH: 'Busca de Slots',
  STATUS_SYNC: 'Sync Status',
};

const MESSAGE_TYPE_CSS: Record<IntegrationMessageType, string> = {
  PATIENT_SYNC: styles.badgePatientSync,
  APPOINTMENT_SYNC: styles.badgeAppointmentSync,
  SLOT_FETCH: styles.badgeSlotFetch,
  STATUS_SYNC: styles.badgeStatusSync,
};

/* ── Filter options ────────────────────────────────────── */

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'PENDING', label: 'Pendente' },
  { value: 'PROCESSING', label: 'Processando' },
  { value: 'COMPLETED', label: 'Concluído' },
  { value: 'FAILED', label: 'Falhou' },
  { value: 'DEAD_LETTER', label: 'Dead Letter' },
];

const TYPE_FILTER_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'PATIENT_SYNC', label: 'Sync Paciente' },
  { value: 'APPOINTMENT_SYNC', label: 'Sync Agendamento' },
  { value: 'SLOT_FETCH', label: 'Busca de Slots' },
  { value: 'STATUS_SYNC', label: 'Sync Status' },
];

/* ── Helper functions ──────────────────────────────────── */

function formatDateTime(isoStr: string): string {
  const date = new Date(isoStr);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatRelativeTime(lastFetchedMs: number): string {
  const seconds = Math.floor((Date.now() - lastFetchedMs) / 1000);
  if (seconds < 5) return 'agora';
  if (seconds < 60) return `${seconds}s atras`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}min atras`;
}

/* ── Component ─────────────────────────────────────────── */

/**
 * Admin dashboard for monitoring integration outbox messages.
 *
 * Features:
 * - Status summary cards with auto-refresh (10s)
 * - Filterable outbox messages table
 * - Collapsible dead-letter section
 * - Detail modal for individual messages
 * - Retry / requeue actions
 */
export function IntegrationDashboardPage() {
  const queryClient = useQueryClient();

  // Filter state
  const [statusFilter, setStatusFilter] = useState<OutboxMessageStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<IntegrationMessageType | ''>('');

  // Detail modal
  const [selectedMessage, setSelectedMessage] = useState<OutboxMessage | null>(null);

  // Dead letters collapsible
  const [isDeadLettersOpen, setIsDeadLettersOpen] = useState(false);

  // Track last refresh time for the indicator
  const [lastRefreshedAt, setLastRefreshedAt] = useState(Date.now());
  const [refreshLabel, setRefreshLabel] = useState('agora');
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Queries ──────────────────────────────────────────

  const {
    data: outboxStatus,
    isLoading: isStatusLoading,
    error: statusError,
  } = useQuery({
    queryKey: ['integration', 'outbox-status'],
    queryFn: () => integrationService.getOutboxStatus(),
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const queryParams = {
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(typeFilter ? { messageType: typeFilter } : {}),
  };

  const {
    data: messages = [],
    isLoading: isMessagesLoading,
    error: messagesError,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['integration', 'outbox-messages', queryParams],
    queryFn: () =>
      integrationService.getOutboxMessages(
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
      ),
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const {
    data: deadLetters = [],
    isLoading: isDeadLettersLoading,
    error: deadLettersError,
  } = useQuery({
    queryKey: ['integration', 'dead-letters'],
    queryFn: () => integrationService.getDeadLetters(),
    refetchInterval: REFRESH_INTERVAL_MS,
    enabled: isDeadLettersOpen,
  });

  // ── Mutations ────────────────────────────────────────

  const retryMutation = useMutation({
    mutationFn: (id: string) => integrationService.retryMessage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration'] });
    },
  });

  const requeueMutation = useMutation({
    mutationFn: (id: string) => integrationService.requeueDeadLetter(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration'] });
    },
  });

  // ── Refresh indicator ────────────────────────────────

  useEffect(() => {
    if (dataUpdatedAt) {
      setLastRefreshedAt(dataUpdatedAt);
    }
  }, [dataUpdatedAt]);

  useEffect(() => {
    const tick = () => {
      setRefreshLabel(formatRelativeTime(lastRefreshedAt));
    };
    tick();
    refreshIntervalRef.current = setInterval(tick, 1000);
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [lastRefreshedAt]);

  // ── Handlers ─────────────────────────────────────────

  const handleRetry = useCallback(
    (event: React.MouseEvent, messageId: string) => {
      event.stopPropagation();
      retryMutation.mutate(messageId);
    },
    [retryMutation],
  );

  const handleRequeue = useCallback(
    (event: React.MouseEvent, deadLetterId: string) => {
      event.stopPropagation();
      requeueMutation.mutate(deadLetterId);
    },
    [requeueMutation],
  );

  const handleRowClick = useCallback((message: OutboxMessage) => {
    setSelectedMessage(message);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedMessage(null);
  }, []);

  const toggleDeadLetters = useCallback(() => {
    setIsDeadLettersOpen((prev) => !prev);
  }, []);

  // ── Outbox table columns ─────────────────────────────

  const outboxColumns = [
    {
      key: 'messageType',
      header: 'Tipo',
      width: '160px',
      render: (msg: OutboxMessage) => (
        <span className={MESSAGE_TYPE_CSS[msg.messageType]}>
          {MESSAGE_TYPE_LABELS[msg.messageType]}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '130px',
      render: (msg: OutboxMessage) => (
        <span className={STATUS_CSS[msg.status]}>
          {STATUS_LABELS[msg.status]}
        </span>
      ),
    },
    {
      key: 'correlationId',
      header: 'Correlation ID',
      width: '180px',
      render: (msg: OutboxMessage) => (
        <span className={styles.correlationId} title={msg.correlationId}>
          {msg.correlationId.length > 20
            ? `${msg.correlationId.slice(0, 20)}...`
            : msg.correlationId}
        </span>
      ),
    },
    {
      key: 'externalId',
      header: 'External ID',
      width: '140px',
      render: (msg: OutboxMessage) => (
        <span className={styles.externalId}>
          {msg.externalId ?? '-'}
        </span>
      ),
    },
    {
      key: 'retries',
      header: 'Tentativas',
      width: '100px',
      render: (msg: OutboxMessage) => (
        <span className={styles.retryInfo}>
          {msg.retryCount}/{msg.maxRetries}
        </span>
      ),
    },
    {
      key: 'lastError',
      header: 'Último Erro',
      width: '200px',
      render: (msg: OutboxMessage) =>
        msg.lastError ? (
          <span className={styles.lastError} title={msg.lastError}>
            {msg.lastError}
          </span>
        ) : (
          <span className={styles.dateText}>-</span>
        ),
    },
    {
      key: 'createdAt',
      header: 'Criado em',
      width: '160px',
      render: (msg: OutboxMessage) => (
        <span className={styles.dateText}>{formatDateTime(msg.createdAt)}</span>
      ),
    },
    {
      key: 'processedAt',
      header: 'Processado em',
      width: '160px',
      render: (msg: OutboxMessage) => (
        <span className={styles.dateText}>
          {msg.processedAt ? formatDateTime(msg.processedAt) : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      width: '140px',
      render: (msg: OutboxMessage) => (
        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMessage(msg);
            }}
            aria-label={`Ver detalhes da mensagem ${msg.id}`}
          >
            <Eye size={14} aria-hidden="true" />
          </Button>
          {msg.status === 'FAILED' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => handleRetry(e, msg.id)}
              disabled={retryMutation.isPending}
              aria-label={`Retentar mensagem ${msg.id}`}
            >
              <RotateCcw size={14} aria-hidden="true" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  // ── Dead letters table columns ───────────────────────

  const deadLetterColumns = [
    {
      key: 'originalMessageId',
      header: 'Mensagem Original',
      render: (dl: DeadLetter) => (
        <span className={styles.correlationId} title={dl.originalMessageId}>
          {dl.originalMessageId.length > 24
            ? `${dl.originalMessageId.slice(0, 24)}...`
            : dl.originalMessageId}
        </span>
      ),
    },
    {
      key: 'messageType',
      header: 'Tipo',
      width: '160px',
      render: (dl: DeadLetter) => (
        <span className={MESSAGE_TYPE_CSS[dl.messageType]}>
          {MESSAGE_TYPE_LABELS[dl.messageType]}
        </span>
      ),
    },
    {
      key: 'lastError',
      header: 'Último Erro',
      render: (dl: DeadLetter) =>
        dl.lastError ? (
          <span className={styles.lastError} title={dl.lastError}>
            {dl.lastError}
          </span>
        ) : (
          <span className={styles.dateText}>-</span>
        ),
    },
    {
      key: 'retryCount',
      header: 'Tentativas',
      width: '100px',
      render: (dl: DeadLetter) => (
        <span className={styles.retryInfo}>{dl.retryCount}</span>
      ),
    },
    {
      key: 'movedAt',
      header: 'Movido em',
      width: '160px',
      render: (dl: DeadLetter) => (
        <span className={styles.dateText}>{formatDateTime(dl.movedAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      width: '100px',
      render: (dl: DeadLetter) => (
        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleRequeue(e, dl.id)}
            disabled={requeueMutation.isPending}
            aria-label={`Reprocessar dead letter ${dl.id}`}
            leftIcon={<RotateCcw size={14} />}
          >
            Reprocessar
          </Button>
        </div>
      ),
    },
  ];

  // ── Error state ──────────────────────────────────────

  if (statusError || messagesError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar dados de integracao. Tente novamente mais tarde.</p>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Integrações</h1>
          <p className={styles.subtitle}>
            Monitore o status das mensagens de integracao do sistema.
          </p>
        </div>
        <div className={styles.headerRight}>
          <span className={styles.refreshIndicator} aria-live="polite">
            <span className={styles.refreshDot} aria-hidden="true" />
            Atualizado {refreshLabel}
          </span>
        </div>
      </header>

      {/* Status Cards */}
      <section aria-label="Resumo do status das integrações">
        {isStatusLoading ? (
          <div className={styles.loadingState} role="status" aria-busy="true">
            <p>Carregando status...</p>
          </div>
        ) : outboxStatus ? (
          <div className={styles.statusCards}>
            <div className={`${styles.statusCard} ${styles.cardPending}`}>
              <div className={styles.statusCardIcon} aria-hidden="true">
                <Clock size={24} />
              </div>
              <div className={styles.statusCardContent}>
                <span className={styles.statusCardCount}>{outboxStatus.pending}</span>
                <span className={styles.statusCardLabel}>Pendentes</span>
              </div>
            </div>

            <div className={`${styles.statusCard} ${styles.cardProcessing}`}>
              <div className={styles.statusCardIcon} aria-hidden="true">
                <Loader size={24} />
              </div>
              <div className={styles.statusCardContent}>
                <span className={styles.statusCardCount}>{outboxStatus.processing}</span>
                <span className={styles.statusCardLabel}>Processando</span>
              </div>
            </div>

            <div className={`${styles.statusCard} ${styles.cardCompleted}`}>
              <div className={styles.statusCardIcon} aria-hidden="true">
                <CheckCircle size={24} />
              </div>
              <div className={styles.statusCardContent}>
                <span className={styles.statusCardCount}>{outboxStatus.completed}</span>
                <span className={styles.statusCardLabel}>Concluídos</span>
              </div>
            </div>

            <div className={`${styles.statusCard} ${styles.cardFailed}`}>
              <div className={styles.statusCardIcon} aria-hidden="true">
                <AlertTriangle size={24} />
              </div>
              <div className={styles.statusCardContent}>
                <span className={styles.statusCardCount}>{outboxStatus.failed}</span>
                <span className={styles.statusCardLabel}>Falhas</span>
              </div>
            </div>

            <div className={`${styles.statusCard} ${styles.cardDeadLetter}`}>
              <div className={styles.statusCardIcon} aria-hidden="true">
                <Skull size={24} />
              </div>
              <div className={styles.statusCardContent}>
                <span className={styles.statusCardCount}>{outboxStatus.deadLetters}</span>
                <span className={styles.statusCardLabel}>Dead Letters</span>
              </div>
            </div>
          </div>
        ) : null}
      </section>

      {/* Outbox Messages Section */}
      <section className={styles.section} aria-label="Mensagens do outbox">
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Mensagens do Outbox</h2>
        </div>

        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <Select
              label="Filtrar por status"
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as OutboxMessageStatus | '')
              }
            />
          </div>
          <div className={styles.filterGroup}>
            <Select
              label="Filtrar por tipo"
              options={TYPE_FILTER_OPTIONS}
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter(e.target.value as IntegrationMessageType | '')
              }
            />
          </div>
        </div>

        {isMessagesLoading ? (
          <div className={styles.loadingState} role="status" aria-busy="true">
            <p>Carregando mensagens...</p>
          </div>
        ) : (
          <Table
            columns={outboxColumns}
            data={messages}
            keyExtractor={(msg) => msg.id}
            onRowClick={handleRowClick}
            emptyMessage="Nenhuma mensagem encontrada."
          />
        )}
      </section>

      {/* Dead Letters Section (Collapsible) */}
      <section className={styles.section} aria-label="Dead letters">
        <button
          type="button"
          className={styles.collapsibleHeader}
          onClick={toggleDeadLetters}
          aria-expanded={isDeadLettersOpen}
          aria-controls="dead-letters-content"
        >
          <span
            className={[
              styles.collapsibleIcon,
              isDeadLettersOpen ? styles.collapsibleIconOpen : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-hidden="true"
          >
            <ChevronRight size={20} />
          </span>
          Dead Letters
          {outboxStatus ? (
            <Badge variant="error">{String(outboxStatus.deadLetters)}</Badge>
          ) : null}
        </button>

        {isDeadLettersOpen && (
          <div id="dead-letters-content" className={styles.collapsibleContent}>
            {deadLettersError ? (
              <div className={styles.errorState} role="alert">
                <p>Erro ao carregar dead letters.</p>
              </div>
            ) : isDeadLettersLoading ? (
              <div className={styles.loadingState} role="status" aria-busy="true">
                <p>Carregando dead letters...</p>
              </div>
            ) : (
              <Table
                columns={deadLetterColumns}
                data={deadLetters}
                keyExtractor={(dl) => dl.id}
                emptyMessage="Nenhuma dead letter encontrada."
              />
            )}
          </div>
        )}
      </section>

      {/* Detail Modal */}
      <Modal
        isOpen={selectedMessage !== null}
        onClose={handleCloseDetail}
        title="Detalhes da Mensagem"
        size="lg"
      >
        {selectedMessage && (
          <div>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>ID</span>
                <span className={styles.detailValueMono}>{selectedMessage.id}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Status</span>
                <span className={STATUS_CSS[selectedMessage.status]}>
                  {STATUS_LABELS[selectedMessage.status]}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Tipo</span>
                <span className={MESSAGE_TYPE_CSS[selectedMessage.messageType]}>
                  {MESSAGE_TYPE_LABELS[selectedMessage.messageType]}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Correlation ID</span>
                <span className={styles.detailValueMono}>
                  {selectedMessage.correlationId}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>External ID</span>
                <span className={styles.detailValueMono}>
                  {selectedMessage.externalId ?? '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Tentativas</span>
                <span className={styles.detailValue}>
                  {selectedMessage.retryCount} / {selectedMessage.maxRetries}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Criado em</span>
                <span className={styles.detailValue}>
                  {formatDateTime(selectedMessage.createdAt)}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Processado em</span>
                <span className={styles.detailValue}>
                  {selectedMessage.processedAt
                    ? formatDateTime(selectedMessage.processedAt)
                    : '-'}
                </span>
              </div>
              {selectedMessage.nextRetryAt && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Próxima Tentativa</span>
                  <span className={styles.detailValue}>
                    {formatDateTime(selectedMessage.nextRetryAt)}
                  </span>
                </div>
              )}
            </div>

            {/* Error details */}
            {selectedMessage.lastError && (
              <>
                <h3 className={styles.detailSectionTitle}>Erro</h3>
                <div className={styles.errorBlock}>
                  {selectedMessage.lastError}
                </div>
              </>
            )}

            {/* Retry timeline */}
            {selectedMessage.retryCount > 0 && (
              <>
                <h3 className={styles.detailSectionTitle}>Histórico de Tentativas</h3>
                <div className={styles.retryTimeline} role="list" aria-label="Histórico de tentativas">
                  {Array.from({ length: selectedMessage.retryCount }, (_, index) => (
                    <div
                      key={index}
                      className={styles.retryTimelineItem}
                      role="listitem"
                    >
                      <span
                        className={[
                          styles.retryTimelineDot,
                          index === selectedMessage.retryCount - 1 &&
                          selectedMessage.status === 'COMPLETED'
                            ? styles.retryTimelineDotSuccess
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        aria-hidden="true"
                      />
                      Tentativa {index + 1} de {selectedMessage.maxRetries}
                      {index === selectedMessage.retryCount - 1 &&
                      selectedMessage.status === 'COMPLETED'
                        ? ' - Sucesso'
                        : ' - Falhou'}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Modal actions */}
            {selectedMessage.status === 'FAILED' && (
              <div className={styles.modalActions}>
                <Button
                  leftIcon={<RotateCcw size={16} />}
                  onClick={(e) => {
                    handleRetry(e, selectedMessage.id);
                    handleCloseDetail();
                  }}
                  disabled={retryMutation.isPending}
                  isLoading={retryMutation.isPending}
                >
                  Retentar
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
